import { FC } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';

import { Button, Link, Stack } from '@mui/material';

import { useSnackbar } from 'notistack';

import { useForgotPasswordMutation } from '@src/store/auth/auth-api-slice';

import { yupResolver } from '@hookform/resolvers/yup';
import CustomTextField from '@src/components/rhf/custom-textfield';
import FormProvider from '@src/components/rhf/form-provider';
import useLocales from '@src/locales/use-locales';
import paths from '@src/routes/routes';

import {
  createForgotPasswordSchema,
  createDefaultValues,
  createFields,
} from './fields';

type ForgotPasswordFormProps = {
  goToStep: (index: number) => void;
};

const ForgotPasswordForm: FC<ForgotPasswordFormProps> = (props) => {
  const { goToStep } = props;
  const { t } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();

  const fields = createFields(t);

  const methods = useForm<{ email: string }>({
    resolver: yupResolver(createForgotPasswordSchema(t)),
    defaultValues: createDefaultValues(t),
  });

  const {
    formState: { isSubmitting },
  } = methods;

  const onSubmit: SubmitHandler<{ email: string }> = async (data) => {
    const hostUrl = `${window.location.protocol}//${window.location.host}`;

    const formData = {
      email: data.email,
      host: hostUrl,
    };

    try {
      await forgotPassword({ ...formData }).unwrap();

      goToStep(1);
    } catch (error: any) {
      enqueueSnackbar(t('notifications.forgotPassword.error'), {
        variant: 'error',
      });
    }
  };

  return (
    <Stack width="100%">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack spacing={2} gap={2}>
          {fields.map((field) => {
            return (
              <CustomTextField
                key={`${field.name} forgot password`}
                name={field.name}
                label={field.label}
                variant="outlined"
              />
            );
          })}

          <Stack
            flexDirection={{ sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ sm: 'center' }}
            gap={2}
          >
            <Link
              component={RouterLink}
              to={paths.signIn}
              variant="body1"
              underline="hover"
            >
              {t('buttons.ReturnToSignIn')}
            </Link>
          </Stack>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            loading={isSubmitting || isLoading}
          >
            {t('buttons.send')}
          </Button>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default ForgotPasswordForm;
